import React, { useState } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon, Cog6ToothIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { usePlayfulToast } from './PlayfulToastProvider';
import { MessageCategory, categoryIcons } from './playfulMessages';

interface PlayfulToastSettingsProps {
    isOpen: boolean;
    onClose: () => void;
}

export const PlayfulToastSettings: React.FC<PlayfulToastSettingsProps> = ({ isOpen, onClose }) => {
    const { settings, updateSettings, triggerRandomMessage } = usePlayfulToast();
    const [localSettings, setLocalSettings] = useState(settings);

    const handleSave = () => {
        updateSettings(localSettings);
        onClose();
    };

    const handleToggleCategory = (category: MessageCategory) => {
        setLocalSettings(prev => {
            const categories = prev.categories.includes(category)
                ? prev.categories.filter(c => c !== category)
                : [...prev.categories, category];
            return { ...prev, categories };
        });
    };

    const intervalOptions = [
        { value: 15, label: '15 minutes' },
        { value: 30, label: '30 minutes' },
        { value: 60, label: '1 hour' },
        { value: 120, label: '2 hours' },
    ];

    const categoryLabels: Record<MessageCategory, string> = {
        sales_wisdom: 'Sales Wisdom',
        coffee_break: 'Coffee Breaks',
        motivation: 'Motivation',
        humor: 'Humor',
        wellness: 'Wellness',
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200]"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
                <div
                    className={clsx(
                        'bg-white rounded-2xl shadow-2xl',
                        'w-full max-w-md max-h-[90vh] overflow-y-auto',
                        'animate-slide-in-right'
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                                <SparklesIcon className="w-4 h-4 text-white" />
                            </div>
                            <h2 className="text-lg font-bold text-neutral-900">Playful Toasts</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5 text-neutral-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4">
                        {/* Enable/Disable Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-neutral-900">Enable Playful Toasts</h3>
                                <p className="text-xs text-neutral-500">Show fun messages periodically</p>
                            </div>
                            <button
                                onClick={() => setLocalSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                                className={clsx(
                                    'relative w-11 h-6 rounded-full transition-colors flex-shrink-0',
                                    localSettings.enabled ? 'bg-purple-500' : 'bg-neutral-300'
                                )}
                            >
                                <div
                                    className={clsx(
                                        'absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform',
                                        localSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    )}
                                />
                            </button>
                        </div>

                        {/* Sound Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-semibold text-neutral-900">Sound Effects</h3>
                                <p className="text-xs text-neutral-500">Play a gentle chime</p>
                            </div>
                            <button
                                onClick={() => setLocalSettings(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }))}
                                className={clsx(
                                    'relative w-11 h-6 rounded-full transition-colors flex-shrink-0',
                                    localSettings.soundEnabled ? 'bg-purple-500' : 'bg-neutral-300'
                                )}
                            >
                                <div
                                    className={clsx(
                                        'absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform',
                                        localSettings.soundEnabled ? 'translate-x-5' : 'translate-x-0.5'
                                    )}
                                />
                            </button>
                        </div>

                        {/* Frequency Selector */}
                        <div>
                            <h3 className="text-sm font-semibold text-neutral-900 mb-2">Frequency</h3>
                            <div className="grid grid-cols-2 gap-2">
                                {intervalOptions.map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => setLocalSettings(prev => ({ ...prev, intervalMinutes: option.value }))}
                                        className={clsx(
                                            'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                            localSettings.intervalMinutes === option.value
                                                ? 'bg-purple-500 text-white shadow-md shadow-purple-500/30'
                                                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Category Selection */}
                        <div>
                            <h3 className="text-sm font-semibold text-neutral-900 mb-2">Categories</h3>
                            <div className="grid grid-cols-2 gap-2">
                                {(Object.keys(categoryLabels) as MessageCategory[]).map(category => (
                                    <label
                                        key={category}
                                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 cursor-pointer transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={localSettings.categories.includes(category)}
                                            onChange={() => handleToggleCategory(category)}
                                            className="w-3.5 h-3.5 text-purple-500 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-lg">{categoryIcons[category]}</span>
                                        <span className="text-xs font-medium text-neutral-700">
                                            {categoryLabels[category]}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between p-4 border-t border-neutral-200 bg-neutral-50 rounded-b-2xl sticky bottom-0">
                        <button
                            onClick={triggerRandomMessage}
                            className="px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        >
                            Test Now
                        </button>
                        <div className="flex gap-2">
                            <button
                                onClick={onClose}
                                className="px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-200 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-purple-500 hover:bg-purple-600 rounded-lg transition-colors shadow-md shadow-purple-500/30"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PlayfulToastSettings;

